import type { SelectChangeEvent } from "@mui/material";
import { MenuItem, Select } from "@mui/material";
import classNames from "classnames";
import { useMemo } from "react";
import { ArrowIcon } from "../../icons/ArrowIcon";
import styles from "./styles.module.css";

interface Props<T> {
  options: T[];
  value: T | null;
  onChange: (value: T | null) => void;
  getLabel: (option: T) => string;
  getValue: (option: T) => string | number;
  emptyOptionLabel?: string;
  isDisabled?: boolean;
  placeholder?: string;
  className?: string;
  selectClassName?: string;
}

export const GenericSelect = <T,>({
  options,
  value,
  onChange,
  getLabel,
  getValue,
  emptyOptionLabel,
  isDisabled,
  placeholder,
  className,
  selectClassName,
}: Props<T>) => {
  const valueToOption = useMemo(() => {
    const map = new Map<string | number, T>();
    options?.forEach((option) => {
      map.set(String(getValue(option)), option);
    });
    return map;
  }, [options, getValue]);

  const handleRenderValue = () => {
    if (!value) {
      return emptyOptionLabel
        ? emptyOptionLabel
        : placeholder
          ? placeholder
          : "";
    } else return getLabel(value);
  };

  const handleChangeOption = (event: SelectChangeEvent<string | number>) => {
    const selectedValue = event.target.value;
    const selected = valueToOption.get(selectedValue) || null;
    onChange(selected);
  };

  const selectValue = value === null ? "" : String(getValue(value));

  return (
    <Select
      value={selectValue}
      IconComponent={ArrowIcon}
      renderValue={handleRenderValue}
      onChange={handleChangeOption}
      className={classNames(styles.select, className)}
      MenuProps={{
        classes: { list: styles["menu-list"], paper: styles["menu-paper"] },
      }}
      classes={{
        select: `${styles["base-select"]} ${isDisabled ? styles.disabled : ""}, ${selectClassName}`,
        icon: `${styles.icon} ${isDisabled ? styles.disabled : ""}`,
      }}
      sx={{
        ".MuiOutlinedInput-notchedOutline": {
          border: 0,
          borderStyle: "none",
        },
      }}
      displayEmpty
      disabled={isDisabled}
    >
      {emptyOptionLabel !== undefined && (
        <MenuItem
          key="empty-option"
          value={""}
          className={classNames(styles.option)}
          classes={{ selected: styles.selected }}
        >
          <em>{emptyOptionLabel}</em>
        </MenuItem>
      )}
      {options.map((option, index) => {
        const stringValue = String(getValue(option));
        return (
          <MenuItem
            key={`${stringValue}-${index}`}
            value={stringValue}
            className={classNames(styles.option)}
            classes={{ selected: styles.selected }}
          >
            {getLabel(option)}
          </MenuItem>
        );
      })}
    </Select>
  );
};
